import queries from '@apis/queries';
import useToast from '@hooks/ui/useToast';
import { ItemOption } from '@models/item';
import { REMOVE_OPTION } from '@store/item/actions/optionActions';
import { itemActions } from '@store/item/itemSlice';
import { RootState } from '@store/store';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

export interface SelectOptionParams {
    option: ItemOption;
    optionGroupId: string;
    amount: number;
}

export interface RemoveOptionParams {
    option: ItemOption;
    optionGroupId: string;
}

interface Props {
    itemId: string;
}

function useOptionControl({ itemId }:Props) {
    const { orderLines } = useSelector((state: RootState) => state.item);
    const { data: item } = useQuery(queries.items.detail(itemId));
    const dispatch = useDispatch();
    const toast = useToast();

    // 옵션 선택 여부 확인
    const isOptionSelected = ({ optionGroupId, optionId }: {optionId: string, optionGroupId: string}): boolean => {
        const optionGroups = orderLines
            .map((it) => [...it.personalOptions, ...it.defaultOptions])
            .flat();
        const optionGroup = optionGroups.find((og) => og.optionGroupId === optionGroupId);
        return optionGroup?.options
            .some((option) => option.shopOptionId === optionId) || false;
    };

    const selectOption = (params: SelectOptionParams) => {
        if (!item) {
            toast.error('상품 정보를 불러올 수 없습니다.');
            return;
        }

        const { optionGroupId, option, amount } = params;

        const allOptionGroups = [...item.defaultOptionGroups, ...item.personalOptionGroups];
        const optionGroup = allOptionGroups
            .find((og) => og.optionGroupId === optionGroupId)!;
        const currentOrderLine = orderLines.find((orderLine) => orderLine.shopItemId === item.id)!;
        const currentSelectionCount = [...currentOrderLine.personalOptions, ...currentOrderLine.defaultOptions]
            .find((og) => og.optionGroupId === optionGroupId)
            ?.options?.reduce((acc, cur) => acc + cur.quantity, 0) || 0;

        if (currentSelectionCount + amount > optionGroup.maxSelection
        && optionGroup.uiButtonType === 'CHECKBOX') {
            toast.error(`최대 선택 가능 개수는 ${optionGroup.maxSelection}개 입니다.`);
            return;
        }
        dispatch(itemActions.updateOptionQuantity({
            option,
            optionGroupId,
            amount,
            item,
        }));
    };

    const removeOption = ({ option, optionGroupId }:RemoveOptionParams) => {
        if (!item) {
            return;
        }
        dispatch(itemActions.updateOptionQuantity({
            option,
            optionGroupId,
            amount: REMOVE_OPTION,
            item,
        }));
    };

    const watchOptionMaxCount = (optionId: string, optionGroupId: string): number => {
        const optionGroup = findOptionGroup(optionGroupId);
        const option = findOption(optionId, optionGroupId);
        if (!optionGroup) {
            return 0;
        }
        const otherOptionsQuantity = optionGroup.options
            .filter((opt) => opt.shopOptionId !== optionId)
            .reduce((sum, opt) => sum + opt.quantity, 0);

        const maxFromGroup = optionGroup.maxSelection - otherOptionsQuantity;
        const maxFromOption = option?.maxQuantity || Number.MAX_VALUE;
        return Math.min(maxFromGroup, maxFromOption);
    };

    const findOptionGroup = (optionGroupId: string) => orderLines
        .map((orderLine) => [
            ...orderLine.defaultOptions,
            ...orderLine.personalOptions,
        ])
        .flat()
        .find((optionWithGroup) => optionWithGroup.optionGroupId === optionGroupId);

    const findOption = (optionId: string, optionGroupId: string) => {
        const optionGroup = findOptionGroup(optionGroupId);
        return optionGroup?.options.find((opt) => opt.shopOptionId === optionId);
    };

    const watchOptionCount = (optionId: string, optionGroupId: string) => findOption(optionId, optionGroupId)?.quantity || 0;

    const resetPersonalOptions = () => {
        dispatch(itemActions.resetPersonalOptions());
    };

    return {
        isOptionSelected,
        selectOption,
        removeOption,
        watchOptionCount,
        watchOptionMaxCount,
        resetPersonalOptions,
    };
}

export default useOptionControl;
